<template>
  <div class="posts-widget">
    <div v-if="loading && !fetchingMore" class="posts-loading">Loading...</div>
    <div v-else class="list-container" ref="listContainer">
      <div v-if="error" class="posts-error">{{ error }}</div>
      <ul v-else class="posts-list">
        <li v-for="post in filteredPosts" :key="post.id" class="post-item">
          <router-link
            :to="`/${post.type}/${post.slug}`"
            class="post-link"
          >
            <div class="post-content">
              <!-- Left: Date in circle -->
              <div class="post-left">
                <div class="post-date-circle">
                  <span class="date-day">{{ formatDay(post.date) }}</span>
                  <span class="date-month">{{ formatMonth(post.date) }}</span>
                </div>
                <div class="post-text">
                  <h3 class="post-title" v-if="post.title && post.title.rendered">
                    {{ post.title.rendered }}
                  </h3>
                  <div class="post-meta">
                    <span v-if="post._embedded && post._embedded.author && post._embedded.author[0]">
                      By {{ post._embedded.author[0].name }}
                    </span>
                  </div>
                </div>
              </div>
              <!-- Middle: Post type -->
              <div class="post-type-badge">
                {{ getPostTypeName(post.type) }}
              </div>
              <!-- Right: View button -->
              <div class="post-right">
                <span class="view-button">View <i class="fas fa-chevron-right" style="margin-bottom: 1px; font-size: 75%;"></i></span>
              </div>
            </div>
          </router-link>
        </li>
      </ul>
      <div v-if="fetchingMore" class="load-more-indicator">
        <span>Loading more...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchCurrentUser, fetchPosts, category_from_role, fetchCategoriesBySlugs } from '@/utils/fetch.js';

export default {
  data() {
    return {
      loading: true,
      error: null,
      allPosts: [],
      filteredPosts: [],
      postTypes: ['announcement', 'process'],
      perPage: 7,
      page: 1,
      hasMore: true,
      fetchingMore: false,
    };
  },
  methods: {
    async loadPosts() {
      this.loading = true;
      this.error = null;
      this.page = 1;
      try {
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);
        // Always include 'general'
        if (!roleCategorySlugs.includes('general')) {
          roleCategorySlugs.push('general');
        }
        let query = `&per_page=${this.perPage}&page=${this.page}&_embed`;
        if (roleCategorySlugs.length) {
          query += `&categories_slug=${roleCategorySlugs.join(',')}`;
        }
        const fetchedPosts = await fetchPosts(query, this.postTypes);
        const allowedSlugs = roleCategorySlugs; // already includes 'general'
        const allowedCategories = await fetchCategoriesBySlugs(allowedSlugs);
        const allowedCategoryIds = allowedCategories.map(cat => cat.id);

        this.allPosts = fetchedPosts.filter(post =>
          post.categories.some(catId => allowedCategoryIds.includes(catId))
        );
        this.filterPosts();
        this.hasMore = this.allPosts.length === this.perPage;
        this.page += 1;
      } catch (err) {
        this.error = `Failed to fetch posts: ${err.message}`;
      } finally {
        this.loading = false;
      }
    },
    async loadMorePosts() {
      if (this.fetchingMore || !this.hasMore) return;
      this.fetchingMore = true;
      try {
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);
        // Always include 'general'
        if (!roleCategorySlugs.includes('general')) {
          roleCategorySlugs.push('general');
        }
        let query = `&per_page=${this.perPage}&page=${this.page}&_embed`;
        if (roleCategorySlugs.length) {
          query += `&categories_slug=${roleCategorySlugs.join(',')}`;
        }
        const fetchedPosts = await fetchPosts(query, this.postTypes);
        const allowedSlugs = roleCategorySlugs; // already includes 'general'
        const allowedCategories = await fetchCategoriesBySlugs(allowedSlugs);
        const allowedCategoryIds = allowedCategories.map(cat => cat.id);

        const morePosts = fetchedPosts.filter(post =>
          post.categories.some(catId => allowedCategoryIds.includes(catId))
        );
        this.allPosts = [...this.allPosts, ...morePosts];
        this.filterPosts();
        this.hasMore = morePosts.length === this.perPage;
        this.page += 1;
      } catch (err) {
        this.error = `Failed to fetch more posts: ${err.message}`;
      } finally {
        this.fetchingMore = false;
      }
    },
    handleScroll() {
      if (!this.$refs.listContainer) return;
      const container = this.$refs.listContainer;
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollThreshold = container.scrollHeight - 200;
      if (scrollPosition >= scrollThreshold && this.hasMore && !this.fetchingMore) {
        this.loadMorePosts();
      }
    },
    filterPosts() {
      // Only announcements and processes
      this.filteredPosts = this.allPosts.filter(
        post => this.postTypes.includes(post.type)
      );
    },
    getPostTypeName(type) {
      switch (type) {
        case 'announcement':
          return 'Announcement';
        case 'process':
          return 'Process';
        default:
          return '';
      }
    },
    formatDay(dateStr) {
      const d = new Date(dateStr);
      return d.getDate().toString().padStart(2, '0');
    },
    formatMonth(dateStr) {
      const d = new Date(dateStr);
      return d.toLocaleString('default', { month: 'short' }).toUpperCase();
    }
  },
  mounted() {
    this.loadPosts();
    if (this.$refs.listContainer) {
      this.$refs.listContainer.addEventListener('scroll', this.handleScroll);
    }
  },
  beforeUnmount() {
    if (this.$refs.listContainer) {
      this.$refs.listContainer.removeEventListener('scroll', this.handleScroll);
    }
  },
  updated() {
    if (this.$refs.listContainer) {
      this.$refs.listContainer.addEventListener('scroll', this.handleScroll);
    }
  }
};
</script>

<style scoped>
.posts-widget {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  width: 100%;
  text-align: center;
  flex: 1 1 0;
  min-height: 0;
  box-sizing: border-box;
  overflow-x: hidden;
}

.posts-list {
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px;
  padding-left: 0;
  margin: 0;
  list-style: none;
  min-height: 0;
}

.list-container {
  height: 100%;
  padding-right: 5px;
  overflow-y: auto;
  overflow-x: hidden;
  --fade-amount: 1.5%;
  mask-image: linear-gradient(
    transparent,
    black var(--fade-amount), 
    black calc(100% - var(--fade-amount)), 
    transparent
  );
}

.post-item {
  padding-bottom: 0.5rem;
}

.post-item:last-child {
  border-bottom: none;
}

.posts-loading,
.posts-error {
  padding: 1rem 0;
  text-align: center;
  color: #888;
}

.post-content {
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  position: relative;
  padding: 0 120px 0 0;
  overflow: hidden;
}

.post-left {
  display: flex;
  align-items: center;
  max-width: 50%;
  min-width: 0;
}

.post-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  border-radius: 25px;
  padding: .5rem;
  transition: background-color 0.2s ease, transform 0.3s ease;
  box-sizing: border-box;
}

.post-link:hover {
  background-color: #F9F8F6F0;
  transform: translateX(5px);
}

.post-date-circle {
  width: 50px;
  height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #025780;
  margin-right: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.date-day {
  font-size: 1.3em;
  line-height: 1;
}

.date-month {
  font-size: 0.85em;
  line-height: 1;
  color: #fff;
  letter-spacing: 1px;
}

.post-text {
  flex: 1;
  text-align: left;
  min-width: 0;
  max-width: 100%;
}

.post-title {
  margin: 0 0 0.25rem;
  color: #5F5E5B;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.post-meta {
  font-size: 0.85em;
  color: #666;
}

.post-type-badge {
  padding: 0.35em 0;
  font-size: 0.85em;
  font-weight: 600;
  color: #94918B;
  margin: 0 auto;
  white-space: nowrap;
  width: 100px;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translateX(50%);
}

.post-right {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
}

.view-button {
  display: inline-block;
  font-size: 0.85em;
  white-space: nowrap;
  padding-right: 1em;
  color: var(--primary-color);
}

.load-more-indicator {
  text-align: center;
  padding: 1rem;
  color: #888;
  font-size: 0.9em;
}

@media (max-width: 1100px) {
  .posts-widget {
    min-height: 400px;
    flex: 1 1 auto;
  }
  .post-title {
    white-space: normal;
  }
  .post-content {
    padding-right: 70px;
  }
}

@media (max-width: 700px) {
  .post-date-circle {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }

  .post-left {
    max-width: 100%;
  }

  .post-type-badge {
    display: none;
  }

  .date-day {
    font-size: 1.1em;
  }

  .date-month {
    font-size: 0.75em;
  }

  .post-title {
    font-size: 1rem;
  }

  .post-meta {
    font-size: 0.75em;
  }

  .post-type-badge {
    width: 80px;
    font-size: 0.75em;
  }

  .view-button {
    font-size: 0.75em;
    padding-right: 0.75em;
  }
}
</style>